import { Link } from 'react-router-dom';
import { products, categories } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import ProductCard from '@/components/feature/ProductCard';
import ArticleCard from '@/components/feature/ArticleCard';

const featuredProducts = products.slice(0, 8);
const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);

export default function Home() {
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '亚洲精品',
    url: 'https://www.yazhoujingpin.com',
    description: '亚洲精品汇聚日韩、东南亚及华系顶尖品牌，为您精选最具品质与设计感的亚洲精品产品。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-21-6888-8888',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressCountry: 'CN',
    },
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '亚洲精品',
    url: 'https://www.yazhoujingpin.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.yazhoujingpin.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Asian%20lifestyle%20scene%20with%20premium%20products%2C%20silk%20fabric%20texture%2C%20ceramic%20tea%20set%2C%20warm%20golden%20amber%20lighting%2C%20cream%20background%2C%20soft%20shadows%2C%20luxurious%20yet%20minimalist%20composition%2C%20editorial%20photography%20style%2C%20Zen%20aesthetic%20with%20modern%20twist&width=1800&height=900&seq=hero-main-bg&orientation=landscape"
          alt="亚洲精品"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/40"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-wide leading-tight max-w-3xl">
            甄选亚洲顶级精品
          </h1>
          <p className="mt-4 md:mt-6 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">
            汇聚日韩、东南亚及华系顶尖品牌，以严苛标准筛选每一件亚洲精品，<br className="hidden md:block" />
            为您呈现最具品质与设计感的精致生活方式
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
            <Link
              to="/categories"
              className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
            >
              探索精品
            </Link>
            <Link
              to="/articles"
              className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/25 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer border border-white/20"
            >
              阅读文章
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Promise */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-shield-check-line', title: '严苛选品标准', desc: '每件产品经过品质、设计、文化三重筛选' },
              { icon: 'ri-global-line', title: '亚洲直采', desc: '直接与日韩、东南亚品牌方合作' },
              { icon: 'ri-truck-line', title: '跨境极速物流', desc: '专业跨境物流，7-14天极速送达' },
              { icon: 'ri-customer-service-2-line', title: '专属客服', desc: '一对一中文客服，全程无忧购物体验' },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="mt-4 text-base font-heading font-semibold text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">探索品类</h2>
            <p className="mt-3 text-foreground-500 text-sm md:text-base">从潮流时尚到居家好物，发现属于你的亚洲精致生活</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/categories?tab=${cat.id}`}
                className="group bg-background-50 rounded-xl p-6 md:p-8 text-center hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-100/80 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                  <i className={`${cat.icon} text-2xl`}></i>
                </div>
                <h3 className="mt-4 text-base font-heading font-semibold text-foreground-900">{cat.name}</h3>
                <p className="mt-2 text-xs md:text-sm text-foreground-500">{cat.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">精选推荐</h2>
              <p className="mt-2 text-foreground-500 text-sm md:text-base">编辑团队为你精心挑选的亚洲好物</p>
            </div>
            <Link
              to="/categories"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Banner */}
      <section className="relative py-16 md:py-24 bg-background-100 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20elegant%20Asian%20style%20textile%20and%20ceramic%20craft%20background%20with%20golden%20amber%20tones%2C%20subtle%20silk%20patterns%2C%20warm%20cream%20gradient%2C%20abstract%20minimalist%20texture%2C%20premium%20brand%20atmosphere&width=1800&height=600&seq=banner-mid&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-medium text-accent-600 bg-accent-50 px-3 py-1.5 rounded-full whitespace-nowrap">品质承诺</span>
          <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-900">每一件亚洲精品，都经过我们的严苛甄选</h2>
          <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            我们走访亚洲各地的品牌工坊与设计师工作室，只为找到那些真正代表亚洲美学高度的作品
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 mt-10">
            {[
              { num: '500+', label: '合作品牌' },
              { num: '20,000+', label: '精选单品' },
              { num: '98%', label: '好评率' },
              { num: '50万+', label: '会员信赖' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.num}</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">精选文章</h2>
              <p className="mt-2 text-foreground-500 text-sm md:text-base">深度了解亚洲品牌故事与生活美学</p>
            </div>
            <Link
              to="/articles"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
            >
              阅读更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-14 md:py-20 bg-foreground-900">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">订阅亚洲精品资讯</h2>
          <p className="mt-3 text-foreground-100 text-sm md:text-base">
            第一时间获取新品上架、独家折扣和亚洲生活方式灵感
          </p>
          <form
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            data-readdy-form
            action="https://readdy.ai/api/form/d9sp4l94ne17ei0sdlg0"
            method="POST"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
              if (honeypot) {
                const msg = document.getElementById('newsletter-msg');
                if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'mt-4 text-sm text-green-400'; }
                form.reset();
                return;
              }
              const formData = new FormData(form);
              const msg = document.getElementById('newsletter-msg');
              try {
                const res = await fetch(form.action, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                  body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                });
                const responseText = await res.text();
                let parsed: Record<string, unknown> = {};
                try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
                const code = (parsed as { code?: string })?.code;
                const meta = (parsed as { meta?: { message?: string; detail?: string } })?.meta;
                const serverMsg = meta?.message || meta?.detail || '';
                if (res.ok && code === 'OK') {
                  if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'mt-4 text-sm text-green-400'; }
                  form.reset();
                } else {
                  const errorMsg = serverMsg || '订阅失败，请稍后重试';
                  if (msg) { msg.textContent = errorMsg; msg.className = 'mt-4 text-sm text-red-400'; }
                }
              } catch {
                if (msg) { msg.textContent = '网络错误，请稍后重试'; msg.className = 'mt-4 text-sm text-red-400'; }
              }
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="请输入您的邮箱"
              required
              className="flex-1 px-5 py-3 bg-foreground-800 border border-foreground-700 rounded-full text-sm text-background-50 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500"
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <button
              type="submit"
              className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
            >
              立即订阅
            </button>
          </form>
          <p id="newsletter-msg" className="mt-4 text-sm"></p>
        </div>
      </section>
    </>
  );
}